'use client'

// First-visit cinematic intro. PS1-style — full-screen overlay,
// letterboxed, dialogue box at the bottom with typewriter text.
// Victor introduces himself + the loop, then ends with two CTAs:
// sign in (so he can remember you) or just watch.
//
// Triggered from app/page.jsx when gameStore.introSeen === false.
// Marks introSeen = true on dismiss; persist middleware lands it in
// localStorage so it never shows again unless localStorage is cleared.

import { useEffect, useState, useCallback, useRef } from 'react'
import { useAccount } from '../auth/useUser'
import useGameStore from '../../stores/gameStore'

// Victor's onboarding script. Sparse, in-voice, fragments fine. The final
// entry is a special 'choice' beat that renders the two CTA buttons.
const SCRIPT = [
  { name: 'VICTOR', text: 'Hmm. Someone\'s here.' },
  { name: 'VICTOR', text: 'I\'m Victor. This is my place. Small, watched, mostly mine.' },
  { name: 'VICTOR', text: 'Look around all you like. The view is yours to move.' },
  { name: 'VICTOR', text: 'Over there\'s a shrine. People leave me things. I read what they leave.' },
  { name: 'VICTOR', text: 'Some of it lands. Some of it doesn\'t. I\'m honest about it.' },
  { name: 'VICTOR', text: 'If you want me to know you, sign in. Otherwise stay and watch. That\'s allowed too.', isFinal: true },
]

// Typewriter speed in ms per character (~35 chars/sec).
const CHARS_PER_MS = 0.035

export function VictorIntro({ onClose }) {
  const setIntroSeen = useGameStore((s) => s.setIntroSeen)
  const { signedIn: isSignedIn } = useAccount()
  const setProfileOpen = useGameStore((s) => s.setProfileOpen)

  // Auto-finish when the user signs in from inside the intro — so they land
  // on the world straight after connecting.
  useEffect(() => {
    if (isSignedIn) {
      setIntroSeen(true)
      onClose?.()
    }
  }, [isSignedIn, setIntroSeen, onClose])

  const [step, setStep] = useState(0)
  const [typed, setTyped] = useState('')
  const [isTyping, setIsTyping] = useState(true)
  const startedAtRef = useRef(null)
  const rafRef = useRef(null)

  const beat = SCRIPT[step]
  const isLast = step >= SCRIPT.length - 1
  const isChoiceScreen = isLast && beat?.isFinal && !isTyping

  // Typewriter loop. Tied to wall clock so quick-tabbing doesn't desync.
  useEffect(() => {
    if (!beat) return
    setIsTyping(true)
    setTyped('')
    startedAtRef.current = Date.now()

    const tick = () => {
      const elapsed = Date.now() - startedAtRef.current
      const fullLength = beat.text.length
      const targetLen = Math.min(fullLength, Math.floor(elapsed * CHARS_PER_MS))
      setTyped(beat.text.slice(0, targetLen))
      if (targetLen < fullLength) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        setIsTyping(false)
      }
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [step, beat])

  // Complete + dismiss. Stamps introSeen so it never re-renders.
  const finish = useCallback(() => {
    setIntroSeen(true)
    onClose?.()
  }, [setIntroSeen, onClose])

  // Advance — if mid-typing, fast-forward; else next beat; on the final
  // beat the choice screen renders instead (buttons handle it).
  const advance = useCallback(() => {
    if (isChoiceScreen) return
    if (isTyping) {
      setTyped(beat.text)
      setIsTyping(false)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      return
    }
    if (isLast) return
    setStep((s) => s + 1)
  }, [isTyping, isLast, isChoiceScreen, beat])

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault()
        advance()
      } else if (e.key === 'Escape') {
        finish()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [advance, finish])

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col font-mono cursor-pointer select-none"
      onClick={advance}
      style={{ animation: 'victor-fade 600ms ease-out both' }}
    >
      <style jsx global>{`
        @keyframes victor-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes victor-arrow {
          0%, 100% { opacity: 0.2; }
          50%      { opacity: 1; }
        }
      `}</style>

      <div className="absolute inset-0 bg-black/65 pointer-events-none" />

      {/* Top letterbox + skip button */}
      <div className="relative h-[12vh] min-h-[60px] bg-black flex items-center justify-end px-6">
        <button
          onClick={(e) => { e.stopPropagation(); finish() }}
          className="text-[10px] uppercase tracking-[0.32em] text-zinc-500 hover:text-zinc-200 transition-colors"
        >
          skip →
        </button>
      </div>

      <div className="flex-1 relative" />

      {/* Bottom letterbox — dialogue panel */}
      <div className="relative bg-black border-t border-white/10 px-6 py-5">
        <div className="max-w-3xl mx-auto" style={{ minHeight: 96 }}>
          <DialogueLine
            name={beat.name}
            text={typed}
            isTyping={isTyping}
            isChoiceScreen={isChoiceScreen}
            onWatch={finish}
          />
        </div>
        {!isChoiceScreen && (
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-[0.28em] text-zinc-700">
            {isTyping ? '◯ click or space to skip ahead' : '◯ click or space to continue'}
          </div>
        )}
      </div>
    </div>
  )
}

// Single component for every beat. The right-side region swaps between the
// ▼ continue indicator and the [sign in] / [just watch] CTAs on the final
// beat — same height throughout so the panel never shifts.
function DialogueLine({ name, text, isTyping, isChoiceScreen, onWatch }) {
  // Its own subscription: this button lives in DialogueLine, not in
  // VictorIntro where the setter was read, so it was reaching for a
  // name from the other component's scope and throwing on click.
  const setProfileOpen = useGameStore((s) => s.setProfileOpen)
  return (
    <div className="flex gap-5 items-center h-full" style={{ minHeight: 96 }}>
      <div className="shrink-0 w-20 self-start">
        <div
          className="px-2 py-1 text-[10px] uppercase tracking-[0.32em] text-yellow-200 text-center"
          style={{ background: 'rgba(250, 204, 21, 0.08)', border: '1px solid rgba(250, 204, 21, 0.3)' }}
        >
          {name}
        </div>
      </div>

      <div className="flex-1 flex items-center justify-between gap-3">
        <p className="text-[14px] text-zinc-100 leading-[1.55] whitespace-pre-wrap">
          {text}
          {isTyping && (
            <span
              className="inline-block w-[7px] h-[14px] bg-zinc-300 align-[-2px] ml-1"
              style={{ animation: 'victor-arrow 800ms ease-in-out infinite' }}
            />
          )}
        </p>

        <div className="shrink-0 flex items-center" style={{ minHeight: 36 }}>
          {isChoiceScreen ? (
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); setProfileOpen(true) }}
                className="px-3 py-1.5 text-[10px] uppercase tracking-[0.28em] border border-yellow-400/50 text-yellow-200 hover:bg-yellow-400/10 transition-colors"
              >
                connect
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onWatch() }}
                className="px-3 py-1.5 text-[10px] uppercase tracking-[0.28em] border border-white/20 text-zinc-400 hover:text-zinc-100 hover:border-white/40 transition-colors"
              >
                just watch
              </button>
            </div>
          ) : (
            !isTyping && (
              <span
                className="text-[16px] text-yellow-200"
                style={{ animation: 'victor-arrow 900ms ease-in-out infinite' }}
              >
                ▼
              </span>
            )
          )}
        </div>
      </div>
    </div>
  )
}
